/**
 * MobileBookingPage — the "Booking" tab.
 *
 * Wraps the SAME <BookingFormModal> the desktop uses, in its full-screen mode.
 * That is a deliberate choice over writing a phone-specific booking form:
 * the wizard carries the customer search, the vehicle ownership-conflict
 * warning, the service-profile vs ad-hoc branch and the two different submit
 * endpoints. A parallel implementation would be a second place for all of that
 * to drift out of step with the backend contract.
 *
 * The tab opens on the workshop's BOOKING LIST — every booking, split into Not
 * completed and Completed and sorted newest first — with a Book button in the header
 * that swaps to the wizard. It used to open straight onto the wizard with no list at
 * all, so a phone could create bookings but never see one.
 *
 * The list chrome is MobileListScreen and the grouping is useGroupedList, both shared
 * with the fleet portal's screens; the tab split itself is bookingGroups.js, so the two
 * booking lists cannot disagree about what "completed" means.
 *
 * There is still no calendar and no edit path here — rescheduling, editing and loan cars
 * stay on the desktop.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CheckCircle, Plus } from 'lucide-react';
import BookingFormModal from '../BookingFormModal';
import MobileListScreen from './MobileListScreen';
import useGroupedList from './useGroupedList';
import { BOOKING_STATUS_KEYS, bookingStatusOptions, groupBookingByStatus } from './bookingGroups';
import {
    clearOperationStatus,
    createBooking,
    createServiceProfileBooking,
    createMileageBooking,
    fetchBookingServiceCategories,
    fetchBookings,
} from '../../store/slices/bookingSlice';
import { CalendarPlus, Clock } from 'lucide-react';

// ── Success screen ────────────────────────────────────────────────────────────

const BookingCreated = ({ booking, onAddAnother, onDone }) => {
    // Date and time are echoed from the created booking rather than the form so
    // what's shown is what the server actually stored.
    const when = [booking?.scheduledDate, booking?.scheduledTime].filter(Boolean).join(' · ');

    return (
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center justify-center px-6 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle size={30} className="text-emerald-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Booking created</h2>
            {booking?.bookingCode && (
                <p className="mt-1 text-sm font-mono text-gray-500">{booking.bookingCode}</p>
            )}

            <div className="mt-4 w-full max-w-xs bg-white rounded-xl border border-gray-100 divide-y divide-gray-50 text-left">
                {booking?.customerName && (
                    <div className="px-4 py-2.5">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400">Customer</p>
                        <p className="text-sm font-medium text-gray-800 truncate">{booking.customerName}</p>
                    </div>
                )}
                {(booking?.vehicleDescription || booking?.vehicleRego) && (
                    <div className="px-4 py-2.5">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400">Vehicle</p>
                        <p className="text-sm font-medium text-gray-800 truncate">
                            {booking.vehicleDescription || booking.vehicleRego}
                        </p>
                    </div>
                )}
                {when && (
                    <div className="px-4 py-2.5">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400">Scheduled</p>
                        <p className="text-sm font-medium text-gray-800">{when}</p>
                    </div>
                )}
            </div>

            <button
                onClick={onAddAnother}
                className="mt-6 w-full max-w-xs flex items-center justify-center gap-1.5 py-3 rounded-xl
                           bg-emerald-600 text-white text-sm font-semibold active:bg-emerald-700 min-h-[48px]"
            >
                <Plus size={16} /> New booking
            </button>

            <button
                onClick={onDone}
                className="mt-2 w-full max-w-xs py-3 rounded-xl border border-gray-200
                           text-sm font-medium text-gray-600 min-h-[48px]"
            >
                Back to bookings
            </button>

            <p className="mt-4 text-xs text-gray-400 max-w-xs leading-relaxed">
                The job is on the workshop board. Rescheduling, editing and loan cars
                are on the desktop.
            </p>
        </div>
    );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const BookingRow = ({ booking }) => (
    <li className="border-b border-gray-100 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-900">
                    {booking.customerName || 'Customer'}
                </p>
                <p className="truncate text-xs text-gray-500">
                    {booking.vehicleDescription || booking.vehicleRego || '—'}
                </p>
                {booking.bookingCode && (
                    <p className="mt-0.5 font-mono text-[11px] text-gray-400">{booking.bookingCode}</p>
                )}
            </div>
            <div className="shrink-0 text-right">
                <p className="text-xs font-medium text-gray-700">{booking.scheduledDate}</p>
                {booking.scheduledTime && (
                    <p className="text-[11px] text-gray-400">{booking.scheduledTime}</p>
                )}
                <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    STATUS_STYLES[booking.status] || 'bg-gray-100 text-gray-600'
                }`}>
                    {statusLabel(booking.status)}
                </span>
            </div>
        </div>
    </li>
);

/** Every value of the server's BookingStatus enum. */
const STATUS_STYLES = {
    SCHEDULED:        'bg-blue-50 text-blue-700',
    JOB_STARTED:      'bg-amber-50 text-amber-700',
    VEHICLE_RECEIVED: 'bg-indigo-50 text-indigo-700',
    COMPLETED:        'bg-gray-100 text-gray-600',
    CANCELLED:        'bg-red-50 text-red-600',
    NO_SHOW:          'bg-red-50 text-red-600',
};

const statusLabel = (s) =>
    (s || '').replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());

const MobileBookingPage = () => {
    const dispatch = useDispatch();

    const {
        bookings,
        bookingsLoading,
        bookingsError,
        bookingServiceCategories,
        metaLoading,
        operationLoading,
        operationError,
    } = useSelector((state) => state.bookings);

    // 'list' until the user taps Book. The wizard is a mode of this tab rather than a
    // separate route, so the bottom tab bar never moves under them.
    const [mode, setMode] = useState('list');

    // The booking just created, or null.
    const [created, setCreated] = useState(null);

    // Bumped to remount the wizard, which is how it gets reset — every field, the found
    // customer and the step counter all live in its local state.
    const [formKey, setFormKey] = useState(0);

    const [segment, setSegment] = useState('open');

    const load = useCallback(() => { dispatch(fetchBookings()); }, [dispatch]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (bookingServiceCategories.length === 0) {
            dispatch(fetchBookingServiceCategories());
        }
    }, [dispatch, bookingServiceCategories.length]);

    // A stale error from a previous attempt would render inside the fresh wizard, so
    // clear it whenever a new one is mounted.
    useEffect(() => {
        dispatch(clearOperationStatus());
    }, [dispatch, formKey]);

    const { groups, counts } = useGroupedList(bookings, {
        keys: BOOKING_STATUS_KEYS,
        groupBy: groupBookingByStatus,
        sortBy: (b) => b.scheduledDate,
    });

    /**
     * Both submit paths funnel through here.
     *
     * The thunk result is inspected directly rather than watching `showCreateModal` in
     * the slice: this page never opened that flag, and the payload is needed for the
     * confirmation screen anyway.
     */
    const submit = (thunk) => async (data) => {
        const result = await dispatch(thunk(data));
        if (thunk.fulfilled.match(result)) {
            setCreated(result.payload);
            // The new booking has to appear in the list behind the confirmation, or
            // going back looks like it was never created.
            load();
        }
        // On rejection the slice sets operationError, which the wizard's final step
        // renders inline — the user stays on step 4 with their input intact.
        return result;
    };

    const startAnother = () => {
        setCreated(null);
        setFormKey((k) => k + 1);
        setMode('create');
    };

    const backToList = () => {
        setCreated(null);
        setMode('list');
        // Land on Not completed: a booking just made is by definition not completed,
        // and landing on Completed would look like it had not been created.
        setSegment('open');
    };

    if (created) {
        return <BookingCreated booking={created} onAddAnother={startAnother} onDone={backToList} />;
    }

    if (mode === 'create') {
        return (
            // `relative` is load-bearing: it is the positioning context for the wizard's
            // `absolute inset-0`, which is what keeps it clear of the bottom tab bar.
            <div className="relative flex-1 min-h-0 flex flex-col">
                <BookingFormModal
                    key={formKey}
                    fullScreen
                    isEdit={false}
                    bookingServiceCategories={bookingServiceCategories}
                    metaLoading={metaLoading}
                    loading={operationLoading}
                    error={operationError}
                    // Closing now returns to the list rather than restarting the wizard.
                    // With no list to return to that used to mean "start over", which
                    // left no way out of the form at all.
                    onClose={backToList}
                    onSubmit={submit(createBooking)}
                    onSubmitServiceProfile={submit(createServiceProfileBooking)}
                    onSubmitMileage={submit(createMileageBooking)}
                />
            </div>
        );
    }

    return (
        <MobileListScreen
            title="Bookings"
            action={(
                <button type="button" onClick={() => setMode('create')} aria-label="New booking"
                        className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-semibold text-white">
                    <Plus size={15} /> Book
                </button>
            )}
            filters={[{
                ariaLabel: 'Booking status',
                options: bookingStatusOptions(counts),
                value: segment,
                onChange: setSegment,
            }]}
            loading={bookingsLoading}
            error={bookingsError}
            onRefresh={load}
            items={groups[segment]}
            renderItem={(b) => <BookingRow key={b.uuid} booking={b} />}
            empty={{
                icon: segment === 'open' ? CalendarPlus : Clock,
                title: segment === 'open' ? 'Nothing outstanding' : 'Nothing completed yet',
                action: segment === 'open' && (
                    <button type="button" onClick={() => setMode('create')}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2.5 text-xs font-semibold text-white">
                        <Plus size={14} /> New booking
                    </button>
                ),
            }}
        />
    );
};

export default MobileBookingPage;
